/**
 * /tools/chaos-scenario-cards?clientId=X — Step 7 tool.
 *
 * The client pre-plans responses to the chaos scenarios that most often
 * derail their bookend (sick day, late night, travel, family crisis,
 * etc.). Each card pairs a SITUATION with a heroic RESPONSE so the
 * client doesn't have to reinvent the response in the moment.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface ScenarioCard {
  situation: string;
  response: string;
}

interface FormState {
  scenarios: ScenarioCard[];
  reflection: string;
}

const EMPTY_CARD: ScenarioCard = { situation: '', response: '' };
const DEFAULT_FORM: FormState = {
  scenarios: [{ ...EMPTY_CARD }, { ...EMPTY_CARD }, { ...EMPTY_CARD }],
  reflection: '',
};

export function ChaosScenarioCardsPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(
          accessToken,
          clientId,
          'chaos-scenario-cards',
        );
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM, scenarios: [{ ...EMPTY_CARD }] };
          if (Array.isArray(p.scenarios) && p.scenarios.length > 0) {
            next.scenarios = (p.scenarios as Record<string, unknown>[]).map((c) => ({
              situation: typeof c.situation === 'string' ? c.situation : '',
              response: typeof c.response === 'string' ? c.response : '',
            }));
          }
          if (typeof p.reflection === 'string') next.reflection = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const setCard = (idx: number, key: keyof ScenarioCard, value: string): void => {
    setForm((prev) => ({
      ...prev,
      scenarios: prev.scenarios.map((c, i) => (i === idx ? { ...c, [key]: value } : c)),
    }));
  };
  const addCard = (): void => {
    setForm((prev) =>
      prev.scenarios.length >= 10
        ? prev
        : { ...prev, scenarios: [...prev.scenarios, { ...EMPTY_CARD }] },
    );
  };
  const removeCard = (idx: number): void => {
    setForm((prev) =>
      prev.scenarios.length <= 1
        ? prev
        : { ...prev, scenarios: prev.scenarios.filter((_, i) => i !== idx) },
    );
  };

  const filled = form.scenarios.filter(
    (c) => c.situation.trim().length > 0 && c.response.trim().length > 0,
  );
  const canSubmit = !submitting && hydrated && filled.length >= 1;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.chaosScenarioCards.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      scenarios: filled.map((c) => ({
        situation: c.situation.trim(),
        response: c.response.trim(),
      })),
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'chaos-scenario-cards',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.chaosScenarioCards.errors.generic'));
      } else {
        setSubmitError(t('tools.chaosScenarioCards.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="chaos-scenario-cards-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.chaosScenarioCards.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.chaosScenarioCards.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.chaosScenarioCards.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.chaosScenarioCards.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.chaosScenarioCards.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <ol className="space-y-s4">
          {form.scenarios.map((card, idx) => (
            <li
              key={idx}
              className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3"
              data-testid={`chaos-scenario-card-${idx}`}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-sm text-gray-1">
                  {t('tools.chaosScenarioCards.card.label', { index: idx + 1 })}
                </span>
                <button
                  type="button"
                  onClick={() => removeCard(idx)}
                  disabled={form.scenarios.length <= 1}
                  className="btn-ghost text-sm"
                  data-testid={`chaos-scenario-card-remove-${idx}`}
                >
                  ×
                </button>
              </div>
              <label className="block">
                <span className="form-label">
                  {t('tools.chaosScenarioCards.card.situation')}
                </span>
                <input
                  type="text"
                  value={card.situation}
                  onChange={(e) => setCard(idx, 'situation', e.target.value)}
                  placeholder={t('tools.chaosScenarioCards.placeholders.situation')}
                  className="input-field w-full mt-s2"
                  data-testid={`chaos-scenario-card-situation-${idx}`}
                />
              </label>
              <label className="block">
                <span className="form-label">
                  {t('tools.chaosScenarioCards.card.response')}
                </span>
                <textarea
                  value={card.response}
                  onChange={(e) => setCard(idx, 'response', e.target.value)}
                  placeholder={t('tools.chaosScenarioCards.placeholders.response')}
                  className="input-field w-full mt-s2 min-h-16"
                  data-testid={`chaos-scenario-card-response-${idx}`}
                  rows={2}
                />
              </label>
            </li>
          ))}
        </ol>
        <button
          type="button"
          onClick={addCard}
          disabled={form.scenarios.length >= 10}
          className="btn-ghost"
          data-testid="chaos-scenario-card-add"
        >
          + {t('tools.chaosScenarioCards.add')}
        </button>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.chaosScenarioCards.reflection.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.chaosScenarioCards.optional')}
              </span>
            </span>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              data-testid="chaos-scenario-cards-reflection"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="chaos-scenario-cards-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="chaos-scenario-cards-saved-at"
          >
            ✓{' '}
            {t('tools.chaosScenarioCards.savedAt', {
              when: savedAt.slice(0, 19).replace('T', ' '),
            })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting
            ? t('tools.chaosScenarioCards.submitting')
            : t('tools.chaosScenarioCards.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
